// =============================
// Email: info@ebenmonney.com
// www.ebenmonney.com/templates
// =============================

import { Component, ChangeDetectorRef, ViewChild, ViewEncapsulation, OnInit, OnDestroy, ElementRef, ChangeDetectionStrategy } from "@angular/core";
import { MediaMatcher } from '@angular/cdk/layout';
import { Router, NavigationStart } from '@angular/router';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatExpansionPanel } from '@angular/material/expansion';
import { MatSidenav } from '@angular/material/sidenav';
import { ToastaService, ToastaConfig, ToastOptions, ToastData } from 'ngx-toasta';

import { AlertService, AlertDialog, DialogType, AlertMessage, MessageSeverity, AlertCommand } from './services/alert.service';
import { AppTranslationService } from "./services/app-translation.service";
import { AccountService } from './services/account.service';
import { LocalStoreManager } from './services/local-store-manager.service';
import { AppTitleService } from './services/app-title.service';
import { AuthService } from './services/auth.service';
import { ConfigurationService } from './services/configuration.service';
import { NavigationService } from './reports/api/navigation.service';

import { Permission } from './models/permission.model';
import { LoginDialogComponent } from "./components/login/login-dialog.component";
import { AppDialogComponent } from './shared/app-dialog.component';
import { NavigationViewModel } from './reports/model/navigationViewModel';
import { UiFunction } from './reports/model/uiFunction';

@Component({
    selector: "app-root",
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent implements OnInit {
    @ViewChild('admin') adminExpander!: MatExpansionPanel;
    @ViewChild('mainNav') mainNav: MatSidenav;

    private readonly _mobileQueryListener: () => void;
    isUserLoggedIn = false;
    isAdminExpanded = false;
    newNotificationCount = 0;
    appTitle = "Rådgivning";
    printing: boolean = false;

    mobileQuery: MediaQueryList;
    stickyToasties: number[] = [];

    uiFunctions: UiFunction[];

    constructor(storageManager: LocalStoreManager,
        private toastaService: ToastaService,
        private toastaConfig: ToastaConfig,
        private accountService: AccountService,
        private alertService: AlertService,
        private authService: AuthService,
        private translationService: AppTranslationService,
        public configurations: ConfigurationService,
        private navigationService: NavigationService,
        public router: Router,
        public dialog: MatDialog,
        changeDetectorRef: ChangeDetectorRef,
        media: MediaMatcher) {
        this.mobileQuery = media.matchMedia('(max-width: 600px)');
        this._mobileQueryListener = () => changeDetectorRef.detectChanges();
        this.addEventListener(this.mobileQuery, this._mobileQueryListener);

        storageManager.initialiseStorageSyncListener();

        translationService.addLanguages(["en", "sv"]);
        translationService.setDefaultLanguage('sv');

        this.toastaConfig.theme = 'material';
        this.toastaConfig.position = 'top-right';
        this.toastaConfig.limit = 100;
        this.toastaConfig.showClose = true;
        this.toastaConfig.showDuration = false;

        AppTitleService.appName = this.appTitle;
    }

    toggleNavigation() {
        this.mainNav.toggle();
    }

    ngOnInit() {
        this.isUserLoggedIn = this.authService.isLoggedIn;

        this.navigationService.getNavigationStructure().subscribe(t => this.navigationLoaded(t.uiFunctions));

        setTimeout(() => {
            if (this.isUserLoggedIn) {
                this.alertService.resetStickyMessage();
                this.alertService.showMessage("Logga in", `Välkommen tillbaka ${this.userName}!`, MessageSeverity.default);
            }
        }, 2000);

        this.alertService.getDialogEvent().subscribe(alert => this.dialog.open(AppDialogComponent,
            {
                minWidth: '40vw',
                data: alert
            }));
        this.alertService.getMessageEvent().subscribe(message => this.showToast(message));

        this.authService.reLoginDelegate = () => this.showLoginDialog();

        this.authService.getLoginStatusEvent().subscribe(isLoggedIn => {
            this.isUserLoggedIn = isLoggedIn;

            setTimeout(() => {
                if (!this.isUserLoggedIn) {
                    this.alertService.showMessage("Sessionen har avslutats!", "", MessageSeverity.default);
                }
            }, 500);
        });

        this.refreshAdminExpanderState(this.router.url);


        this.router.events.subscribe(event => {
            if (event instanceof NavigationStart) {
                let url = (event as NavigationStart).url;

                this.refreshAdminExpanderState(url);

                if (url.includes("/print/")) {
                    this.printing = true;
                } else {
                    this.printing = false;
                }
            }
        });
    }

    ngOnDestroy() {
        this.removeEventListener(this.mobileQuery, this._mobileQueryListener);
    }

    private addEventListener(mediaQuery: MediaQueryList, listener: () => void) {
        if (typeof mediaQuery.addEventListener === "function") {
            mediaQuery.addEventListener("change", listener)
        } else {
            mediaQuery.addListener(listener); // https://github.com/mdn/sprints/issues/858 for Safari < 14
        }
    }

    private removeEventListener(mediaQuery: MediaQueryList, listener: () => void) {
        if (typeof mediaQuery.removeEventListener === "function") {
            mediaQuery.removeEventListener("change", listener,)
        } else {
            mediaQuery.removeListener(listener); // https://github.com/mdn/sprints/issues/858 for Safari < 14
        }
    }

    private refreshAdminExpanderState(currentUrl: string) {
        setTimeout(() => {
            if (this.adminExpander && currentUrl.toLowerCase().indexOf('admin') > 0) {
                this.adminExpander.open();
            }
        }, 200);
    }

    private navigationLoaded(uiFunctions: UiFunction[]) {
        this.uiFunctions = uiFunctions;
    }

    showLoginDialog(): void {
        this.alertService.showStickyMessage("Sessionen är slut", "Sessionen har timat ut. V.v. logga in igen.", MessageSeverity.info);

        let dialogRef = this.dialog.open(LoginDialogComponent, { minWidth: 600 });

        dialogRef.afterClosed().subscribe(result => {
            this.alertService.resetStickyMessage();

            if (!result || this.authService.isSessionExpired) {
                this.authService.logout();
                this.router.navigateByUrl('/login');
                this.alertService.showStickyMessage("Sessionen är slut", "Sessionen har timat ut. V.v. logga in igen.", MessageSeverity.warn);
            }
        });
    }

    showToast(alert: AlertCommand) {

        if (alert.operation === 'clear') {
            for (const id of this.stickyToasties.slice(0)) {
                this.toastaService.clear(id);
            }

            return;
        }

        const toastOptions: ToastOptions = {
            title: alert.message?.summary,
            msg: alert.message?.detail,
        };


        if (alert.operation === 'add_sticky') {
            toastOptions.timeout = 0;

            toastOptions.onAdd = (toast: ToastData) => {
                this.stickyToasties.push(toast.id);
            };

            toastOptions.onRemove = (toast: ToastData) => {
                const index = this.stickyToasties.indexOf(toast.id, 0);

                if (index > -1) {
                    this.stickyToasties.splice(index, 1);
                }

                if (alert.onRemove) {
                    alert.onRemove();
                }

                toast.onAdd = undefined;
                toast.onRemove = undefined;
            };
        } else {
            toastOptions.timeout = 4000;
        }


        switch (alert.message?.severity) {
            case MessageSeverity.default: this.toastaService.default(toastOptions); break;
            case MessageSeverity.info: this.toastaService.info(toastOptions); break;
            case MessageSeverity.success: this.toastaService.success(toastOptions); break;
            case MessageSeverity.error: this.toastaService.error(toastOptions); break;
            case MessageSeverity.warn: this.toastaService.warning(toastOptions); break;
            case MessageSeverity.wait: this.toastaService.wait(toastOptions); break;
        }
    }

    logout() {
        this.authService.logout();
        this.authService.redirectLogoutUser();
    }

    get userName(): string {
        return this.authService.currentUser?.userName ?? '';
    }

    get fullName(): string {
        return this.authService.currentUser?.fullName ?? '';
    }


    get canViewUsers() {
        return this.accountService.userHasPermission(Permission.viewUsersPermission);
    }

    get canViewRoles() {
        return this.accountService.userHasPermission(Permission.viewRolesPermission);
    }

    hasAccessToFunction(uiFunction: UiFunction): boolean {
        if (uiFunction.roles.includes("*")) {
            return true;
        }

        if (!this.authService.isLoggedIn) {
            return false;
        }

        for (let role of this.authService.currentUser.roles) {
            if (uiFunction.roles.includes(role)) {
                return true;
            }
        }

        return false;
    }

    getFunctionTitle(uiFunction: UiFunction): string {
        if (uiFunction.translate) {
            return this.translationService.getTranslation(uiFunction.name);
        } else {
            return uiFunction.name;
        }
    }

}
